import { action } from "@ember/object"
import Component from "@glimmer/component"
import type {
  Group,
  GroupMembershipPotential,
  GroupMembershipRequested,
  IndividualMembership,
} from "@shared/models/group"
import { CURRENT_SCHOOL_YEAR_ID } from "@shared/models/school-year"
import BelongTo from "cc-frontend/helpers/belong-to"
import FindDoc from "cc-frontend/helpers/find-doc"
import FindManyAltogether from "cc-frontend/helpers/find-many-altogether"
import LiveQueryResource from "cc-frontend/helpers/live-query"
import { use } from "ember-could-get-used-to-this"
import { filter, includes } from "lodash-es"
import { tracked } from "tracked-built-ins"

interface Signature {
  Args: {
    group: Group
    parentGroup?: Group | null
    schoolYearIds?: Array<string>
    teamIds?: Array<string>
    memberships: Array<IndividualMembership | GroupMembershipRequested | GroupMembershipPotential>
    columns: {
      id: string
      name?: string
      hasHeaderComponent?: boolean
      cssClass?: string
      maxWidth?: number | string
      width?: number | string
      minWidth?: string | number
    }[]
  }
}

export const AVATAR_COLUMN = {
  id: "AVATAR",
  name: "",
  valuePath: "id",
  hasHeaderComponent: false,
  minWidth: 60,
  width: 60,
  maxWidth: 60,
}

export const NAME_COLUMN = {
  id: "NAME_EMAIL",
  valuePath: "id",
  hasHeaderComponent: true,
  cssClass: "group-members-table__name-cell",
}

export const UPGRADED_ADMIN_COLUMN = {
  id: "UPGRADED_ADMIN",
  valuePath: "attributes.userId",
  hasHeaderComponent: true,
  cssClass: "group-members-table__upgraded-cell",
  width: "90",
}

export const UPGRADED_MEMBER_COLUMN = {
  id: "UPGRADED_MEMBER",
  valuePath: "attributes.userId",
  hasHeaderComponent: true,
  cssClass: "group-members-table__upgraded-cell",
}

export const TEAM_COLUMN = {
  id: "TEAM_COLUMN",
  valuePath: "A",
  hasHeaderComponent: true,
  cssClass: "group-members-table__teams-cell",
  minWidth: "200",
}

export const SHARED_PLANBOOKS_COLUMN = {
  id: "SHARED_PLANBOOKS",
  valuePath: "id",
  hasHeaderComponent: true,
  cssClass: "group-members-table__planbooks-cell",
  minWidth: "250",
  width: "250",
}

export const ACTIONS_CHANGE_ROLE = {
  id: "ACTIONS_CHANGE_ROLE",
  name: "",
  valuePath: "attributes.userId",
  hasHeaderComponent: false,
  cssClass: "group-members-table__actions-cell-admin",
}

/**
 * Used in place of ACTIONS_CHANGE_ROLE in the school-network version.
 * It only shows the Accept buttons, not the change role buttons.
 */
export const ACTIONS_ACCEPT_COLUMN = {
  id: "ACTIONS_ACCEPT",
  name: "",
  valuePath: "attributes.userId",
  hasHeaderComponent: false,
  cssClass: "group-members-table__actions-cell-admin",
}

export const ACTIONS_ALL_COLUMN = {
  id: "ACTIONS_REMOVE_USER",
  name: "",
  valuePath: "attributes.userId",
  hasHeaderComponent: false,
  cssClass: "group-members-table__actions-cell",
}

export default class GroupMembersTable extends Component<Signature> {
  @tracked selectedSchoolYearId = CURRENT_SCHOOL_YEAR_ID

  @use fiscalGroup = new BelongTo(() => [this.args.group, "fiscalGroup"])
  @use schoolYears = new FindManyAltogether(() => {
    let ids = this.args.schoolYearIds || []
    return ["school-year", ids]
  })
  @use selectedSchoolYear = new FindDoc(() => ["school-year", this.selectedSchoolYearId])
  @use planbooksQuery = new LiveQueryResource(() => {
    let queryId = this.args.group.id + ":" + this.selectedSchoolYearId
    return ["planbooks_for_groups_and_school_year", queryId]
  })

  get adminMemberships() {
    return filter(this.args.memberships, (membership) =>
      includes(membership.attributes.roles, "group:is-admin")
    )
  }

  get schoolMemberships() {
    return filter(this.args.memberships, (membership) =>
      includes(membership.attributes.roles, "group:is-member")
    )
  }

  @action
  selectSchoolYear(schoolYear) {
    this.selectedSchoolYearId = schoolYear.id
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Group::MembersTable": typeof GroupMembersTable
  }
}
